import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT, type Lang } from '../i18n';
import { api } from '../api/client';

interface ProfileMenuProps {
  onNavigateProfile: () => void;
}

/**
 * User tag in the topbar, expanded into a small profile dropdown: shows the
 * user's name/email, a link to the full profile page, the canonical app-language
 * choice, and logout. Language changes apply immediately and persist server-side
 * (the server value is canonical and wins over localStorage on next load).
 */
export function ProfileMenu({ onNavigateProfile }: ProfileMenuProps) {
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chooseLang = (next: Lang) => {
    setLang(next); // apply immediately (also persisted in localStorage)
    api.updateProfile({ lang: next }).catch(() => {}); // persist server-side; UI already updated
  };

  if (!user) return null;

  return (
    <div className="profile-menu" ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-tag profile-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('profile.menuAria')}
        title={t('profile.title')}
        style={{ cursor: 'pointer', background: 'none', border: 'none' }}
      >
        <div className="user-initial">{user.initials}</div>
        <span>{user.name}</span>
        <ChevronDown size={12} style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div
          className="profile-menu-panel"
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 220,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r8, 8px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: 6,
            zIndex: 100,
          }}
        >
          <div style={{ padding: '8px 10px' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--t-14)' }}>{user.name}</div>
            {user.email && <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{user.email}</div>}
          </div>
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <button
            type="button"
            role="menuitem"
            className="profile-menu-item"
            onClick={() => { setOpen(false); onNavigateProfile(); }}
            style={menuItemStyle}
          >
            <User size={14} />
            <span>{t('shell.myProfile')}</span>
          </button>
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <div className="text-ink-3" style={{ padding: '4px 10px', fontSize: 'var(--t-11)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('common.language')}
          </div>
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={lang === l.code}
              className="profile-menu-item"
              onClick={() => chooseLang(l.code)}
              style={{ ...menuItemStyle, justifyContent: 'space-between' }}
            >
              <span>{l.label}</span>
              {lang === l.code && <Check size={14} />}
            </button>
          ))}
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <button
            type="button"
            role="menuitem"
            className="profile-menu-item"
            onClick={() => { setOpen(false); logout(); }}
            style={menuItemStyle}
          >
            <LogOut size={14} />
            <span>{t('common.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 10px',
  background: 'none',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 'var(--t-13)',
  color: 'var(--ink)',
  textAlign: 'left',
};
